import React, { useEffect, useState } from "react";
import { Mic2, Save, Volume2, VolumeX } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Slider } from "../../components/ui/slider";
import { toast } from "../../components/ui/sonner";
import { useStudioAuth } from "../StudioAuthContext";
import { Panel, SkeletonRows, StatusChip } from "../ui";

export const AudioSection = () => {
  const { request } = useStudioAuth();
  const [channels, setChannels] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    request("/operations/audio").then(async (response) => {
      if (response.ok) setChannels((await response.json()).channels || []);
    });
  }, [request]);

  const update = (id, patch) => setChannels((items) => items.map((item) => (item.id === id ? { ...item, ...patch } : item)));

  const save = async () => {
    setSaving(true);
    const response = await request("/operations/audio", { method: "POST", body: JSON.stringify({ channels }) });
    setSaving(false);
    if (response.ok) toast.success("Audio mix saved");
    else toast.error("Audio mix could not be saved");
  };

  const allMuted = (channels || []).every((channel) => channel.muted);
  const masterMute = () => setChannels((items) => items.map((item) => ({ ...item, muted: !allMuted })));

  return (
    <Panel
      title="Audio Mixer"
      icon={Mic2}
      note="Levels reflect saved state · no hardware meters simulated"
      actions={
        <>
          <button data-testid="audio-master-mute-button" onClick={masterMute} className="mono h-7 border border-white/10 px-2 text-[9px] font-bold uppercase tracking-widest text-slate-300 transition-colors hover:bg-white/5 hover:text-white">
            {allMuted ? "Unmute all" : "Mute all"}
          </button>
          <Button data-testid="save-audio-mix-button" onClick={save} disabled={saving || !channels} className="h-7 rounded-none bg-[#1a5cff] px-2.5 text-[9px] font-bold uppercase tracking-widest hover:bg-[#2a6cff]">
            <Save className="h-3.5 w-3.5" /> {saving ? "Saving" : "Save mix"}
          </Button>
        </>
      }
      bodyClass="p-0"
    >
      {!channels ? (
        <SkeletonRows rows={4} />
      ) : (
        <div className="grid grid-cols-2 divide-x divide-y divide-[rgba(120,170,255,0.12)] md:grid-cols-4 md:divide-y-0">
          {channels.map((channel) => (
            <div key={channel.id} data-testid={`audio-channel-${channel.id}`} className="flex flex-col items-center px-3 py-4">
              <div className="display text-sm font-black uppercase text-white">{channel.name}</div>
              <StatusChip state={channel.connected ? "ok" : "idle"} className="mt-1.5">{channel.connected ? "Connected" : "No input"}</StatusChip>
              <div className="my-4 flex h-44 items-center">
                <Slider data-testid={`audio-level-${channel.id}`} orientation="vertical" value={[channel.level]} max={100} step={1}
                  onValueChange={([level]) => update(channel.id, { level })}
                  className="h-40 w-5 flex-col [&_[role=slider]]:border-sky-400 [&_[role=slider]]:bg-white" />
              </div>
              <div className="mono text-lg font-bold tabular-nums text-white">{channel.level}</div>
              <button data-testid={`audio-mute-${channel.id}`} onClick={() => update(channel.id, { muted: !channel.muted })}
                className={`mono mt-3 flex h-8 w-full items-center justify-center gap-1.5 border text-[9px] font-bold uppercase tracking-widest transition-colors ${channel.muted ? "border-red-500/50 bg-red-500/15 text-red-300" : "border-white/10 text-slate-400 hover:bg-white/5 hover:text-white"}`}>
                {channel.muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />} {channel.muted ? "Muted" : "Mute"}
              </button>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};

export default AudioSection;
